import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client'

import BaseDialog from '../../../components/dialog'
import iconTimes from '../../../assets/images/icon-times.svg'
import { notify } from '../../../components/toastify'
import { MESSAGES, STATUS } from '../../../constants/messages'
import { FormInput } from '../../../components/partials/formInput'
import { FormInputPhoneNumber } from '../../../components/partials/formInputPhoneNumber'
import ButtonBase from '../../../components/partials/button'
import {
  addressPattern,
  americanPhoneNumberPattern,
  cityPattern,
  contactNamePattern,
  emailPattern,
  namePattern,
  statePattern,
  zipcodePattern,
} from '../../../utils/regex'
import { EDIT_PRINCIPAL_DETAIL } from '../../../queries/adminPortal'
import { useSession } from '../../../helpers/checkSession'
import { openPrincipalAccountDetail } from '../../../redux/actions/adminPortal'
import { customStyles } from '../../../constants/customStyles'
import { UPDATE_PRINCIPAL_ACCOUNT_FOR_MANAGER } from '../../../queries/developerPortal'

export type EditPrincipalAccountFormFields = {
  principalAccountName: string
  principalAccountBusinessEmail: string
  principalAccountContactName: string
  principalAccountPhoneNumber: number
  principalAccountAddressLine1: string
  principalAccountAddressLine2: string
  principalAccountCity: string
  principalAccountState: string
  principalAccountZipcode: number
}

interface PropsAction {
  modalIsOpen?: boolean
  hiddenBlock?: string
  handleEdit?: () => void
  handleCloseDialog?: (val: boolean) => void
  principalAccountDetailInfo?: object | any
  setIsLoadingDataEdited?: (val: boolean) => void
  updateByManager?: boolean,
  setPrincipalInfoResponse?: (data: any) => void
}

const UpdatePrincipalAccount: React.FunctionComponent<PropsAction> = ({
  modalIsOpen,
  principalAccountDetailInfo,
  setIsLoadingDataEdited = () => false,
  handleCloseDialog = () => null,
  updateByManager = false,
  setPrincipalInfoResponse = () => {}
}) => {
  const {
    reset,
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useForm<EditPrincipalAccountFormFields>()
  const { handleErrorSession } = useSession()
  const [UpdatePrincipalAccount] = useMutation(EDIT_PRINCIPAL_DETAIL)
  const [updatePrincipalAccountForManager] = useMutation(UPDATE_PRINCIPAL_ACCOUNT_FOR_MANAGER)
  const dispatch = useDispatch()

  const onSubmit = handleSubmit(async (data) => {
    const variables = {
      principalAccountId: principalAccountDetailInfo?.principalAccountId,
      data: {
        principalAccountName: data?.principalAccountName.trim(),
        principalAccountBusinessEmail: data?.principalAccountBusinessEmail.trim(),
        principalAccountContactName: data?.principalAccountContactName.trim(),
        principalAccountAddressLine1: data?.principalAccountAddressLine1.trim(),
        principalAccountAddressLine2: data?.principalAccountAddressLine2.trim(),
        principalAccountCity: data?.principalAccountCity.trim(),
        principalAccountState: data?.principalAccountState.trim(),
        principalAccountZipcode: data?.principalAccountZipcode,
        principalAccountPhoneNumber: data?.principalAccountPhoneNumber,
      },
    }
    try {
      const dataRes = updateByManager
      ? await updatePrincipalAccountForManager({ variables })
      : await UpdatePrincipalAccount({ variables })
      if (dataRes) {
        notify(MESSAGES.SUCCESS.S_EDIT_PRINCIPAL_ACCOUNT, STATUS.SUCCESS)
        setIsLoadingDataEdited(true)
        handleCloseDialog(false)
        if (updateByManager)
          setPrincipalInfoResponse(dataRes?.data?.updatePrincipalAccountForManager)
        else
          dispatch(
            openPrincipalAccountDetail(dataRes?.data?.updatePrincipalAccount?.principalAccountName),
          )
      }
    } catch (error) {
      handleErrorSession(error, MESSAGES.ERROR.E_CREATED_GROUP_P_TO_PACC)
    }
  })

  const handleTrimValuesEdit = () => {
    setValue('principalAccountName', getValues('principalAccountName').trim())
    setValue('principalAccountBusinessEmail', getValues('principalAccountBusinessEmail').trim())
    setValue('principalAccountContactName', getValues('principalAccountContactName').trim())
    setValue('principalAccountAddressLine1', getValues('principalAccountAddressLine1').trim())
    setValue('principalAccountAddressLine2', getValues('principalAccountAddressLine2').trim())
    setValue('principalAccountCity', getValues('principalAccountCity').trim())
    setValue('principalAccountState', getValues('principalAccountState').trim())
  }

  const handleEdit = async () => {
    await handleTrimValuesEdit()
    await onSubmit()
  }

  const handleCloseEditPrincipalAccountDialog = () => {
    reset()
    handleCloseDialog(false)
  }

  useEffect(() => {
    setValue('principalAccountName', principalAccountDetailInfo?.principalAccountName)
    setValue('principalAccountContactName', principalAccountDetailInfo?.principalAccountContactName)
    setValue(
      'principalAccountBusinessEmail',
      principalAccountDetailInfo?.principalAccountBusinessEmail,
    )
    setValue(
      'principalAccountAddressLine1',
      principalAccountDetailInfo?.principalAccountAddressLine1,
    )
    setValue('principalAccountPhoneNumber', principalAccountDetailInfo?.principalAccountPhoneNumber)
    setValue(
      'principalAccountAddressLine2',
      principalAccountDetailInfo?.principalAccountAddressLine2,
    )
    setValue('principalAccountState', principalAccountDetailInfo?.principalAccountState)
    setValue('principalAccountCity', principalAccountDetailInfo?.principalAccountCity)
    setValue('principalAccountZipcode', principalAccountDetailInfo?.principalAccountZipcode)
  }, [principalAccountDetailInfo])

  return (
    <BaseDialog modalIsOpen={modalIsOpen} customStyles={customStyles}>
      <div className="w-[658px] h-[836px] p-2.5 pt-0">
        <div className="flex justify-between">
          <div />
          <img
            src={iconTimes}
            alt="iconTime"
            className="cursor-pointer mr-[-12px]"
            onClick={handleCloseEditPrincipalAccountDialog}
          />
        </div>
        <p className="text-headline4 mb-2 mt-[-16px]">Edit Principal Account</p>
        <hr className="border-t-neutral-5 mt-4" />{' '}
        <div
          className={`${MESSAGES ? 'cus-scrollbar h-[694px]' : ''} overflow-y-scroll mr-[-23px]`}
        >
          <form onSubmit={handleEdit}>
            <div className="grid grid-cols-1 gap-2">
              <div className="mt-2">
                <p className="text-hairline2 mb-2 mt-3">
                  Name<span className="text-indicator-red">&nbsp;*</span>
                </p>
                <FormInput<EditPrincipalAccountFormFields>
                  id="principalAccountName"
                  type="text"
                  name="principalAccountName"
                  label="Name"
                  placeholder="Enter principal account name"
                  className="mb-1"
                  inputClassName="w-[636px]"
                  register={register}
                  rules={{
                    required: MESSAGES.ERROR.E_REQUIRED_FIELD,
                    pattern: namePattern,
                    maxLength: 200,
                  }}
                  errors={errors}
                  defaultValue={principalAccountDetailInfo?.principalAccountName}
                />
              </div>
              <div className="mt-2">
                <p className="text-hairline2 mb-2">Business email</p>
                <FormInput<EditPrincipalAccountFormFields>
                  id="principalAccountBusinessEmail"
                  type="email"
                  name="principalAccountBusinessEmail"
                  label="Business Email"
                  placeholder="Enter email with your company domain"
                  className="mb-1"
                  inputClassName="w-[636px]"
                  register={register}
                  rules={{
                    pattern: emailPattern,
                    maxLength: 200,
                  }}
                  errors={errors}
                  defaultValue={principalAccountDetailInfo?.principalAccountBusinessEmail}
                />
              </div>
              <div className="mt-2">
                <p className="text-hairline2 mb-2">Contact name</p>
                <FormInput<EditPrincipalAccountFormFields>
                  id="principalAccountContactName"
                  type="text"
                  name="principalAccountContactName"
                  label="Contract Name"
                  placeholder="Enter your contact name"
                  className="mb-2"
                  inputClassName="w-[636px]"
                  register={register}
                  rules={{
                    pattern: contactNamePattern,
                    maxLength: 200,
                  }}
                  errors={errors}
                  defaultValue={principalAccountDetailInfo?.principalAccountContactName}
                />
              </div>
              <div className="mt-2">
                <p className="text-hairline2 mb-2">Phone number</p>
                <FormInputPhoneNumber<EditPrincipalAccountFormFields>
                  control={control}
                  id="principalAccountPhoneNumber"
                  type="text"
                  name="principalAccountPhoneNumber"
                  label="Phone Number"
                  placeholder="Enter your phone number"
                  className="mb-2"
                  inputClassName="w-[636px]"
                  register={register}
                  rules={{
                    pattern: americanPhoneNumberPattern,
                  }}
                  errors={errors}
                  defaultValue={principalAccountDetailInfo?.principalAccountPhoneNumber}
                />
              </div>
              <div className="mt-2">
                <p className="text-hairline2 mb-2">Address line 1</p>
                <FormInput<EditPrincipalAccountFormFields>
                  id="principalAccountAddressLine1"
                  type="text"
                  name="principalAccountAddressLine1"
                  label="Address Line 1"
                  placeholder="Enter your address"
                  className="mb-2"
                  inputClassName="w-[636px]"
                  register={register}
                  rules={{
                    pattern: addressPattern,
                    maxLength: 500,
                  }}
                  errors={errors}
                  defaultValue={principalAccountDetailInfo?.principalAccountAddressLine1}
                />
              </div>
              <div className="mt-2">
                <p className="text-hairline2 mb-2">Address line 2</p>
                <FormInput<EditPrincipalAccountFormFields>
                  id="principalAccountAddressLine2"
                  type="text"
                  name="principalAccountAddressLine2"
                  label="Address Line 2"
                  placeholder="Enter your address"
                  className="mb-2"
                  inputClassName="w-[636px]"
                  register={register}
                  rules={{
                    pattern: addressPattern,
                    maxLength: 500,
                  }}
                  errors={errors}
                  defaultValue={principalAccountDetailInfo?.principalAccountAddressLine2}
                />
              </div>
              <div className="grid grid-cols-3 mt-1 mb-4">
                <div>
                  <p className="text-hairline2 mb-2">City</p>
                  <FormInput<EditPrincipalAccountFormFields>
                    id="principalAccountCity"
                    type="text"
                    name="principalAccountCity"
                    label="City"
                    placeholder="Enter your city"
                    className="mb-2"
                    inputClassName="w-[199.67px]"
                    register={register}
                    rules={{
                      pattern: cityPattern,
                      maxLength: 200,
                    }}
                    errors={errors}
                    defaultValue={principalAccountDetailInfo?.principalAccountCity}
                  />
                </div>
                <div>
                  <p className="text-hairline2 mb-2">State</p>
                  <FormInput<EditPrincipalAccountFormFields>
                    id="principalAccountState"
                    type="text"
                    name="principalAccountState"
                    label="State"
                    placeholder="Enter your state"
                    className="mb-2"
                    inputClassName="w-[199.67px]"
                    register={register}
                    rules={{
                      pattern: statePattern,
                      maxLength: 200,
                    }}
                    errors={errors}
                    defaultValue={principalAccountDetailInfo?.principalAccountState}
                  />
                </div>
                <div>
                  <p className="text-hairline2 mb-2">Zipcode</p>
                  <FormInputPhoneNumber<EditPrincipalAccountFormFields>
                    control={control}
                    id="principalAccountZipcode"
                    type="text"
                    name="principalAccountZipcode"
                    label="Zipcode"
                    placeholder="XXXXX"
                    className="mb-2"
                    inputClassName="w-[199.67px]"
                    register={register}
                    rules={{
                      pattern: zipcodePattern,
                    }}
                    errors={errors}
                    maxLength={5}
                    defaultValue={principalAccountDetailInfo?.principalAccountZipcode}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
        <hr className="border-t-neutral-6 mb-2" />
        <div className="flex w-[636px] pb-0">
          <ButtonBase
            type="submit"
            className="w-[312px] mt-4 bg-primary-1 min-w-min text-center"
            onClick={handleEdit}
            bgDisabled="bg-primary-shade3"
          >
            Save
          </ButtonBase>
          <ButtonBase
            type="submit"
            className="w-[312px] mt-4 ml-3 justify-around border-2 border-primary-1 text-primary-1 bg-neutral-8 hover:bg-primary-shade4"
            onClick={handleCloseEditPrincipalAccountDialog}
            bgDisabled="bg-primary-shade3"
          >
            Cancel
          </ButtonBase>
        </div>
      </div>
    </BaseDialog>
  )
}

export default UpdatePrincipalAccount
